import { useCallback, useRef } from 'react';
import { message } from 'antd';
import {
    EXECUTION_STATUS_TERMINAL,
    EXECUTION_STATUS_SUCCEEDED,
    EXECUTION_POLL_INTERVAL_MS,
} from '../constants/canvas';
import {
    executeFlowGroup,
    executeFlowNode,
    getFlowExecutionBatch,
} from '../execution';
import {
    getExecutionPollRetryDelay,
    resolveNodeExecutionSelectedOutputId,
} from '../utils/execution';
import {
    getErrorMessage,
    resolveExecutionIdentifier,
} from '../utils/common';
import { getNodeValidationError, syncConnectedTargetNodes } from '../utils/validation';
import eventBus from '@utils/eventBus';

/**
 * 画布节点/分组执行与轮询管理
 */
export const useCanvasExecution = ({
    activeId,
    setNodes,
    setSelectedNodeIds,
    nodesRef,
    edgesRef,
    reactFlowRef,
    persistCanvasBatch,
    syncProjectVersion,
    isGroupLocked,
    activeGroupId,
    runGroupSkipSelect,
}) => {
    const executionPollerRef = useRef({});
    const executionRequestAbortRef = useRef({});
    const executionPollFailureCountRef = useRef({});
    const pageActiveRef = useRef(true);

    // 停止指定轮询任务及其请求
    const stopExecutionPolling = useCallback((pollerKey) => {
        const timer = executionPollerRef.current[pollerKey];
        if (timer) {
            clearTimeout(timer);
            delete executionPollerRef.current[pollerKey];
        }

        const controller = executionRequestAbortRef.current[pollerKey];
        if (controller) {
            controller.abort();
            delete executionRequestAbortRef.current[pollerKey];
        }
        delete executionPollFailureCountRef.current[pollerKey];
    }, []);

    // 停止单个节点的执行轮询
    const stopNodeExecutionPolling = useCallback((nodeId) => {
        stopExecutionPolling(nodeId);
    }, [stopExecutionPolling]);

    // 停止当前页面上的所有执行轮询请求
    const stopAllExecutionPolling = useCallback(() => {
        Object.values(executionPollerRef.current).forEach((timer) => clearTimeout(timer));
        executionPollerRef.current = {};
        Object.values(executionRequestAbortRef.current).forEach((controller) => controller?.abort?.());
        executionRequestAbortRef.current = {};
        executionPollFailureCountRef.current = {};
    }, []);

    // 局部更新节点执行态相关字段
    const updateNodeExecutionState = useCallback((nodeId, patch) => {
        setNodes((prev) => prev.map((node) => (
            node.id === nodeId
                ? {
                    ...node,
                    data: {
                        ...node.data,
                        ...patch,
                    },
                }
                : node
        )));
    }, [setNodes]);

    // 轮询单个节点的执行进度并回写结果
    const pollNodeExecution = useCallback(async (nodeId, executionId) => {
        console.log('[FreeCanvas][pollNodeExecution] called, nodeId:', nodeId, 'executionId:', executionId, 'pageActive:', pageActiveRef.current);
        if (!pageActiveRef.current) return;

        const controller = new AbortController();
        executionRequestAbortRef.current[nodeId]?.abort?.();
        executionRequestAbortRef.current[nodeId] = controller;

        try {
            const executionBatch = await getFlowExecutionBatch(executionId, controller.signal);
            if (!pageActiveRef.current || executionRequestAbortRef.current[nodeId] !== controller) return;
            delete executionRequestAbortRef.current[nodeId];
            executionPollFailureCountRef.current[nodeId] = 0;

            const executionList = Array.isArray(executionBatch)
                ? executionBatch
                : Array.isArray(executionBatch?.items)
                    ? executionBatch.items
                    : Array.isArray(executionBatch?.executions)
                        ? executionBatch.executions
                        : [executionBatch].filter(Boolean);
            const executionDetail = executionList.find((item) => String(item?.id ?? '') === String(executionId)) || executionList[0] || {};
            const nodeExecution = executionDetail?.nodeExecutions?.find((item) => String(item?.nodeId ?? '') === String(nodeId)) || null;
            const executionDetailStatus = String(executionDetail?.status || 'processing').toLowerCase();
            const nextStatus = nodeExecution?.status || (
                EXECUTION_STATUS_TERMINAL.has(executionDetailStatus)
                && !EXECUTION_STATUS_SUCCEEDED.has(executionDetailStatus)
                    ? executionDetailStatus
                    : 'processing'
            );

            if (!pageActiveRef.current) return;

            const beforeNodes = nodesRef.current;
            const beforeEdges = edgesRef.current;
            const targetNodeLabel = beforeNodes.find((node) => node.id === nodeId)?.data?.label || '节点';
            const updatedNodes = beforeNodes.map((node) => (
                node.id === nodeId
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            latestExecution: nodeExecution,
                            executionStatus: nextStatus,
                            activeExecutionId: nodeExecution?.executionId || executionId || node.data?.activeExecutionId || '',
                            selectedOutputId: resolveNodeExecutionSelectedOutputId(
                                nodeExecution,
                                node.data?.selectedOutputId,
                            ),
                        },
                    }
                    : node
            ));
            const shouldSyncTargets = String(nextStatus).toLowerCase() === 'succeeded';
            const nextNodes = shouldSyncTargets
                ? syncConnectedTargetNodes(updatedNodes, beforeEdges, { sourceNodeId: nodeId })
                : updatedNodes;

            setNodes(nextNodes);

            if (shouldSyncTargets && JSON.stringify(nextNodes) !== JSON.stringify(beforeNodes)) {
                persistCanvasBatch({
                    beforeNodes,
                    beforeEdges,
                    afterNodes: nextNodes,
                    afterEdges: beforeEdges,
                });
            }

            if (executionDetail?.projectVersion !== undefined && executionDetail?.projectVersion !== null) {
                syncProjectVersion(activeId, Number(executionDetail.projectVersion));
            }

            if (EXECUTION_STATUS_TERMINAL.has(String(nextStatus).toLowerCase())) {
                stopNodeExecutionPolling(nodeId);
                if (String(nextStatus).toLowerCase() === 'failed') {
                    message.warning(
                        nodeExecution?.errMsg
                        || executionDetail?.stopReason
                        || `${targetNodeLabel}执行失败`,
                    );
                }
                return;
            }

            if (pageActiveRef.current) {
                executionPollerRef.current[nodeId] = setTimeout(() => {
                    pollNodeExecution(nodeId, executionId);
                }, EXECUTION_POLL_INTERVAL_MS);
            }
        } catch (error) {
            if (executionRequestAbortRef.current[nodeId] === controller) {
                delete executionRequestAbortRef.current[nodeId];
            }
            if (error?.code === 'ERR_CANCELED' || error?.message === 'canceled' || !pageActiveRef.current) {
                return;
            }
            const failureCount = (executionPollFailureCountRef.current[nodeId] || 0) + 1;
            executionPollFailureCountRef.current[nodeId] = failureCount;
            const retryDelay = getExecutionPollRetryDelay(failureCount);
            console.warn('[FreeCanvas][pollNodeExecution] query failed, retrying:', {
                nodeId,
                executionId,
                failureCount,
                retryDelay,
                error,
            });
            executionPollerRef.current[nodeId] = setTimeout(() => {
                pollNodeExecution(nodeId, executionId);
            }, retryDelay);
        }
    }, [activeId, edgesRef, nodesRef, persistCanvasBatch, setNodes, stopNodeExecutionPolling, syncProjectVersion]);

    // 执行单个功能节点
    const handleExecuteNode = useCallback(async (nodeId) => {
        if (!activeId) return;

        stopNodeExecutionPolling(nodeId);
        updateNodeExecutionState(nodeId, {
            executionStatus: 'processing',
            latestExecution: null,
            activeExecutionId: '',
            selectedOutputId: '',
        });

        try {
            const execution = await executeFlowNode({
                projectId: activeId,
                nodeId,
            });
            console.log('[FreeCanvas][handleExecuteNode] executeFlowNode response:', execution);
            const executionId = resolveExecutionIdentifier(execution);
            console.log('[FreeCanvas][handleExecuteNode] resolved executionId:', executionId);
            if (!executionId) {
                throw new Error('执行节点失败，未返回 executionId');
            }

            eventBus.emit('updatePower');

            const currentNodes = nodesRef.current;
            const currentEdges = edgesRef.current;
            const nextNodes = currentNodes.map((node) => (
                node.id === nodeId
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            latestExecution: null,
                            executionStatus: 'processing',
                            activeExecutionId: String(executionId),
                            selectedOutputId: '',
                        },
                    }
                    : node
            ));
            setNodes(nextNodes);
            persistCanvasBatch({
                beforeNodes: currentNodes,
                beforeEdges: currentEdges,
                afterNodes: nextNodes,
                afterEdges: currentEdges,
            });
            console.log('[FreeCanvas][handleExecuteNode] starting pollNodeExecution, nodeId:', nodeId, 'executionId:', executionId);
            pollNodeExecution(nodeId, executionId);
        } catch (error) {
            console.error('[FreeCanvas][handleExecuteNode] error:', error);
            message.error(getErrorMessage(error, '执行节点失败'));
            const currentNodes = nodesRef.current;
            setNodes(currentNodes.map((node) => (
                node.id === nodeId
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            executionStatus: 'failed',
                            activeExecutionId: '',
                        },
                    }
                    : node
            )));
        }
    }, [activeId, edgesRef, nodesRef, persistCanvasBatch, pollNodeExecution, setNodes, stopNodeExecutionPolling, updateNodeExecutionState]);

    // 轮询分组执行状态，并同步组内节点结果
    const pollGroupExecution = useCallback(async (groupNodeId, executionId, groupFunctionNodeIds, groupChildNodeIds) => {
        if (!pageActiveRef.current) return;

        const pollerKey = `group:${groupNodeId}`;
        const groupChildNodeIdSet = new Set((groupChildNodeIds || []).map((item) => String(item)));
        const controller = new AbortController();
        executionRequestAbortRef.current[pollerKey]?.abort?.();
        executionRequestAbortRef.current[pollerKey] = controller;

        try {
            const executionBatch = await getFlowExecutionBatch(executionId, controller.signal);
            if (!pageActiveRef.current || executionRequestAbortRef.current[pollerKey] !== controller) return;
            delete executionRequestAbortRef.current[pollerKey];
            executionPollFailureCountRef.current[pollerKey] = 0;

            const executionList = Array.isArray(executionBatch)
                ? executionBatch
                : Array.isArray(executionBatch?.items)
                    ? executionBatch.items
                    : Array.isArray(executionBatch?.executions)
                        ? executionBatch.executions
                        : [executionBatch].filter(Boolean);
            const executionDetail = executionList.find((item) => String(item?.id ?? '') === String(executionId)) || executionList[0] || {};
            const executionStatusLower = String(executionDetail?.status || 'processing').toLowerCase();
            const nodeExecutionMap = new Map(
                (executionDetail?.nodeExecutions || []).map((item) => [String(item?.nodeId ?? ''), {
                    ...item,
                    executionId: item?.executionId || executionId,
                }]),
            );
            const targetNodeIdSet = new Set((groupFunctionNodeIds || []).map((item) => String(item)));
            const hasAllTerminalNodeResults = [...targetNodeIdSet].every((nodeId) => {
                const nodeExecution = nodeExecutionMap.get(nodeId);
                return nodeExecution && EXECUTION_STATUS_TERMINAL.has(String(nodeExecution.status || '').toLowerCase());
            });
            const isGroupExecutionTerminal = EXECUTION_STATUS_TERMINAL.has(executionStatusLower)
                && (
                    !EXECUTION_STATUS_SUCCEEDED.has(executionStatusLower)
                    || hasAllTerminalNodeResults
                );
            const shouldContinuePolling = !isGroupExecutionTerminal && pageActiveRef.current;

            const beforeNodes = nodesRef.current;
            const beforeEdges = edgesRef.current;
            let hasFailedNode = false;
            const failedLabels = [];
            const succeededSourceNodeIds = [];

            const updatedNodes = beforeNodes.map((node) => {
                if (String(node.id) === String(groupNodeId)) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            groupExecutionLocked: true,
                            activeExecutionId: String(executionId),
                        },
                    };
                }

                if (!targetNodeIdSet.has(String(node.id))) return node;

                const nodeExecution = nodeExecutionMap.get(String(node.id)) || null;
                const nextStatus = nodeExecution?.status || 'idle';
                const isSucceeded = String(nextStatus).toLowerCase() === 'succeeded';
                const isFailed = String(nextStatus).toLowerCase() === 'failed';

                if (isSucceeded) {
                    succeededSourceNodeIds.push(String(node.id));
                }

                if (isFailed) {
                    hasFailedNode = true;
                    failedLabels.push(node.data?.label || '节点');
                }

                return {
                    ...node,
                    data: {
                        ...node.data,
                        latestExecution: nodeExecution || null,
                        executionStatus: nextStatus,
                        groupExecutionLocked: true,
                        activeExecutionId: nodeExecution?.executionId || executionId || node.data?.activeExecutionId || '',
                        selectedOutputId: nodeExecution
                            ? resolveNodeExecutionSelectedOutputId(
                                nodeExecution,
                                '',
                            )
                            : '',
                    },
                };
            });

            const nextNodes = succeededSourceNodeIds.reduce(
                (acc, sourceNodeId) => syncConnectedTargetNodes(acc, beforeEdges, { sourceNodeId }),
                updatedNodes,
            );

            if (shouldContinuePolling) {
                executionPollerRef.current[pollerKey] = setTimeout(() => {
                    pollGroupExecution(groupNodeId, executionId, groupFunctionNodeIds, groupChildNodeIds);
                }, EXECUTION_POLL_INTERVAL_MS);
            }

            setNodes(nextNodes);

            if (JSON.stringify(nextNodes) !== JSON.stringify(beforeNodes)) {
                persistCanvasBatch({
                    beforeNodes,
                    beforeEdges,
                    afterNodes: nextNodes,
                    afterEdges: beforeEdges,
                });
            }

            if (executionDetail?.projectVersion !== undefined && executionDetail?.projectVersion !== null) {
                syncProjectVersion(activeId, Number(executionDetail.projectVersion));
            }

            const allTerminal = isGroupExecutionTerminal;

            if (allTerminal) {
                stopExecutionPolling(pollerKey);
                setNodes((prev) => prev.map((node) => (
                    String(node.id) === String(groupNodeId) || groupChildNodeIdSet.has(String(node.id))
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                groupExecutionLocked: false,
                                ...(String(node.id) === String(groupNodeId) ? { activeExecutionId: String(executionId) } : {}),
                            },
                        }
                        : node
                )));
                if (hasFailedNode) {
                    const failedLabelText = failedLabels.length > 0 ? failedLabels.join('、') : '某节点';
                    message.warning(
                        executionDetail?.nodeExecutions?.find((item) => String(item?.status).toLowerCase() === 'failed')?.errMsg
                        || executionDetail?.stopReason
                        || `${failedLabelText}执行失败`,
                    );
                }
                return;
            }

        } catch (error) {
            if (executionRequestAbortRef.current[pollerKey] === controller) {
                delete executionRequestAbortRef.current[pollerKey];
            }
            if (error?.code === 'ERR_CANCELED' || error?.message === 'canceled' || !pageActiveRef.current) {
                return;
            }
            const failureCount = (executionPollFailureCountRef.current[pollerKey] || 0) + 1;
            executionPollFailureCountRef.current[pollerKey] = failureCount;
            const retryDelay = getExecutionPollRetryDelay(failureCount);
            console.warn('[FreeCanvas][pollGroupExecution] query failed, retrying:', {
                groupNodeId,
                executionId,
                failureCount,
                retryDelay,
                error,
            });
            executionPollerRef.current[pollerKey] = setTimeout(() => {
                pollGroupExecution(groupNodeId, executionId, groupFunctionNodeIds, groupChildNodeIds);
            }, retryDelay);
        }
    }, [activeId, edgesRef, nodesRef, persistCanvasBatch, setNodes, stopExecutionPolling, syncProjectVersion]);

    // 执行当前激活分组内的功能节点
    const handleRunGroup = useCallback(async () => {
        if (!activeId || !activeGroupId) return;
        if (isGroupLocked(activeGroupId)) return;

        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const groupChildNodes = currentNodes.filter((node) => node.parentId === activeGroupId);
        const groupChildNodeIds = currentNodes
            .filter((node) => node.parentId === activeGroupId)
            .map((node) => node.id);
        const groupFunctionNodeIds = currentNodes
            .filter((node) => node.parentId === activeGroupId && node.type === 'functionNode')
            .map((node) => node.id);

        if (groupFunctionNodeIds.length === 0) {
            message.warning('当前分组内没有可执行的功能节点');
            return;
        }

        const validationError = groupChildNodes
            .map((node) => getNodeValidationError(node))
            .find(Boolean);
        if (validationError) {
            message.error(validationError);
            return;
        }

        const groupPollerKey = `group:${activeGroupId}`;
        stopExecutionPolling(groupPollerKey);
        groupFunctionNodeIds.forEach((nodeId) => stopNodeExecutionPolling(nodeId));

        setNodes((prev) => prev.map((node) => (
            node.id === activeGroupId || groupChildNodeIds.includes(node.id)
                ? {
                    ...node,
                    data: {
                        ...node.data,
                        groupExecutionLocked: true,
                        ...(node.id === activeGroupId ? { activeExecutionId: '' } : {}),
                        ...(groupFunctionNodeIds.includes(node.id)
                            ? {
                                executionStatus: 'idle',
                                latestExecution: null,
                                activeExecutionId: '',
                                selectedOutputId: '',
                            }
                            : {}),
                    },
                }
                : node
        )));

        try {
            const execution = await executeFlowGroup({
                projectId: activeId,
                groupNodeId: activeGroupId,
                manual: !runGroupSkipSelect,
            });
            const executionId = resolveExecutionIdentifier(execution);
            if (!executionId) {
                throw new Error('执行分组失败，未返回 executionId');
            }

            const initialNodeExecutionMap = new Map(
                (execution?.nodeExecutions || []).map((item) => [String(item?.nodeId ?? ''), {
                    ...item,
                    executionId: item?.executionId || executionId,
                }]),
            );
            const nextNodes = currentNodes.map((node) => {
                if (node.id !== activeGroupId && !groupChildNodeIds.includes(node.id)) return node;

                if (!groupFunctionNodeIds.includes(node.id)) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            groupExecutionLocked: true,
                            ...(node.id === activeGroupId ? { activeExecutionId: String(executionId) } : {}),
                        },
                    };
                }

                const nodeExecution = initialNodeExecutionMap.get(String(node.id)) || null;
                return {
                    ...node,
                    data: {
                        ...node.data,
                        groupExecutionLocked: true,
                        latestExecution: nodeExecution || null,
                        executionStatus: nodeExecution?.status || 'idle',
                        activeExecutionId: nodeExecution?.executionId || String(executionId),
                        selectedOutputId: nodeExecution
                            ? resolveNodeExecutionSelectedOutputId(
                                nodeExecution,
                                '',
                            )
                            : '',
                        },
                    };
            });
            setNodes(nextNodes);
            persistCanvasBatch({
                beforeNodes: currentNodes,
                beforeEdges: currentEdges,
                afterNodes: nextNodes,
                afterEdges: currentEdges,
            });

            pollGroupExecution(activeGroupId, executionId, groupFunctionNodeIds, groupChildNodeIds);
        } catch (error) {
            console.error(error);
            message.error(getErrorMessage(error, '执行分组失败'));
            setNodes(currentNodes.map((node) => (
                node.id === activeGroupId || groupChildNodeIds.includes(node.id)
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            groupExecutionLocked: false,
                            ...(node.id === activeGroupId ? { activeExecutionId: '' } : {}),
                            ...(groupFunctionNodeIds.includes(node.id) ? {
                                executionStatus: 'failed',
                                activeExecutionId: '',
                            } : {}),
                        },
                    }
                    : node
            )));
        }
    }, [activeGroupId, activeId, edgesRef, isGroupLocked, nodesRef, persistCanvasBatch, pollGroupExecution, runGroupSkipSelect, setNodes, stopExecutionPolling, stopNodeExecutionPolling]);

    // 根据 executionId 定位并高亮对应节点
    const focusNodeByExecutionId = useCallback((executionId) => {
        const normalizedExecutionId = String(executionId || '');
        if (!normalizedExecutionId) return false;

        const targetNode = nodesRef.current.find((node) => (
            String(
                node?.data?.latestExecution?.executionId
                || node?.latestExecution?.executionId
                || '',
            ) === normalizedExecutionId
        ));

        if (!targetNode?.id) return false;

        setSelectedNodeIds([targetNode.id]);
        setNodes((prev) => prev.map((node) => ({
            ...node,
            selected: node.id === targetNode.id,
        })));

        requestAnimationFrame(() => {
            reactFlowRef.current?.fitView?.({
                nodes: [{ id: targetNode.id }],
                duration: 300,
                padding: 0.4,
                maxZoom: 1.2,
            });
        });

        return true;
    }, [nodesRef, reactFlowRef, setNodes, setSelectedNodeIds]);

    return {
        executionPollerRef,
        executionRequestAbortRef,
        executionPollFailureCountRef,
        pageActiveRef,
        stopExecutionPolling,
        stopNodeExecutionPolling,
        stopAllExecutionPolling,
        updateNodeExecutionState,
        pollNodeExecution,
        handleExecuteNode,
        pollGroupExecution,
        handleRunGroup,
        focusNodeByExecutionId,
    };
};